import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, useLanguage } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from "recharts";

const HabitDetail = () => {
  const { habitId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [habit, setHabit] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bottomTicks, setBottomTicks] = useState([]);
  const [topTicks, setTopTicks] = useState([]);
  const [showAllDates, setShowAllDates] = useState(false);

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(() => {
    const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 3600 * 1000);
    return getLocalDate(thirtyDaysAgo);
  });
  const [endDate, setEndDate] = useState(() => getLocalDate());

  const loadHabit = async () => {
    try {
      const res = await apiClient.get("/habits");
      const h = (res.data || []).find(x => String(x.habit_id) === String(habitId));
      setHabit(h || null);
    } catch (error) {
      console.error("Error loading habit:", error);
    }
  };

  const loadSeries = async () => {
    try {
      setLoading(true);
      try {
        const res = await apiClient.get("/habits/habit-series", { params: { habit_id: habitId, start_date: startDate, end_date: endDate } });
        const s = (res.data || []).map(item => ({ date: item.date, value: item.done ? 1 : 0 }));
        const normalized = Array.from(new Map(s.map((item) => [item.date, item.value])).entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([date, value]) => ({ date, value }));
        setSeries(normalized);
      } catch {
        const dates = [];
        const sDate = new Date(startDate);
        const eDate = new Date(endDate);
        for (let d = new Date(sDate); d <= eDate; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
        const requests = dates.map(d => {
          const dateStr = getLocalDate(d);
          return apiClient.get(`/habits/logs/${dateStr}`).then(r => ({ date: dateStr, logs: r.data || [] })).catch(() => ({ date: dateStr, logs: [] }));
        });
        const results = await Promise.all(requests);
        const s = results.map(r => ({
          date: r.date,
          value: (r.logs || []).some(l => String(l.habit_id) === String(habitId)) ? 1 : 0,
        }));
        const normalized = Array.from(new Map(s.map((item) => [item.date, item.value])).entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([date, value]) => ({ date, value }));
        setSeries(normalized);
      }
    } catch (error) {
      console.error("Error loading series:", error);
      toast.error(isEnglish ? "Failed to load chart" : "Gagal memuat grafik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHabit();
  }, [habitId]);

  useEffect(() => {
    loadSeries();
  }, [habitId, startDate, endDate]);

  useEffect(() => {
    const computeTicks = () => {
      if (!series || series.length === 0) {
        setBottomTicks([]);
        setTopTicks([]);
        return;
      }
      const uniqueDates = Array.from(new Set(series.map((s) => s.date)));
      const tt = [];
      const monthSeen = new Set();
      uniqueDates.forEach((dateStr) => {
        const d = new Date(dateStr);
        const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
        if (!monthSeen.has(monthKey)) {
          monthSeen.add(monthKey);
          tt.push(dateStr);
        }
      });
      const bt = uniqueDates;
      setBottomTicks(bt);
      setTopTicks(tt);
    };
    computeTicks();
  }, [series, showAllDates]);

  const formatMonthYear = (dateStr) => {
    const d = new Date(dateStr);
    return isEnglish
      ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
      : d.toLocaleDateString("id-ID", { month: "short", year: "numeric" });
  };

  const CustomDotLabel = (props) => {
    const { x, y, payload } = props;
    if (payload === undefined) return null;
    const date = new Date(payload.date);
    const day = date.getDate();
    const value = payload.value;

    // Position label below the dot
    const labelY = y + 20;

    return (
      <g>
        <text
          x={x}
          y={labelY}
          textAnchor="middle"
          fontSize={11}
          fill={value === 1 ? "#16a34a" : "#dc2626"}
          fontWeight={500}
        >
          {day}
        </text>
      </g>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{isEnglish ? "Habit Detail" : "Detail Kebiasaan"}</h1>
          {habit && <p className="text-muted-foreground">{habit.name}</p>}
        </div>
        <Button variant="outline" onClick={() => navigate("/habits")}>{isEnglish ? "Back" : "Kembali"}</Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-sm">{isEnglish ? "Start Date" : "Tanggal Mulai"}</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-sm">{isEnglish ? "End Date" : "Tanggal Akhir"}</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
                const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 3600 * 1000);
                setStartDate(getLocalDate(thirtyDaysAgo));
                setEndDate(getLocalDate());
              }}>
                {isEnglish ? "Last 30 days" : "30 hari terakhir"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">{isEnglish ? "Habit Progress Chart" : "Grafik Progress Kebiasaan"}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllDates(!showAllDates)}
              className="text-xs"
            >
              {showAllDates
                ? (isEnglish ? "Show Weekly" : "Tampilkan Mingguan")
                : (isEnglish ? "Show All Dates" : "Tampilkan Semua Tanggal")}
            </Button>
          </div>
          <div className="h-72">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : series.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground text-sm">{isEnglish ? "No data" : "Tidak ada data"}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 50, right: 10, left: -20, bottom: 40 }} className="habit-detail-chart">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    ticks={bottomTicks}
                    allowDuplicatedCategory={false}
                    tickFormatter={(v) => new Date(v).getDate()}
                    interval={0}
                    tick={{ fontSize: showAllDates ? 9 : 10, fill: "#6b7280" }}
                    tickMargin={8}
                    dy={10}
                  />
                  <XAxis
                    dataKey="date"
                    ticks={topTicks}
                    allowDuplicatedCategory={false}
                    tickFormatter={formatMonthYear}
                    orientation="top"
                    axisLine={false}
                    tickLine={false}
                    dy={-5}
                    fontSize={11}
                    fontWeight={500}
                  />
                  <YAxis
                    domain={[0, 1]}
                    ticks={[0, 1]}
                    tickFormatter={(v) => (v === 1 ? (isEnglish ? "Yes" : "Ya") : (isEnglish ? "No" : "Tidak"))}
                  />
                  <RechartsTooltip
                    formatter={(value) => [(value === 1 ? (isEnglish ? "Yes" : "Ya") : (isEnglish ? "No" : "Tidak")), isEnglish ? "Done" : "Dilakukan"]}
                    labelFormatter={(label) => {
                      const d = new Date(label);
                      return isEnglish
                        ? d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
                        : d.toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    dot={{ r: showAllDates ? 3 : 4 }}
                    activeDot={{ r: 6 }}
                    label={<CustomDotLabel />}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HabitDetail;
